import { LotStatus } from '@parallel-fluidics/shared/constants';
import { lotFromFirestore } from '@parallel-fluidics/shared/models/lots';
import {
  collection,
  getDocs,
  getFirestore,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

const ACTIVE_STATUSES = [
  LotStatus.AWAITING_INSPECTION,
  LotStatus.SEALED,
  LotStatus.OPEN,
  LotStatus.QUARANTINED,
];

/**
 * Every lot feeding the By-item overview, deliberately unpaginated: the
 * aggregation must see every matching bag or its counts are wrong, and
 * the fetch is bounded by what's physically on shelves (Empty bags are
 * excluded unless asked for). Ordered newest-first at the source so
 * neither the overview rows nor their expanded bags need a client sort
 * — filter combinations are served by index merging over the per-field
 * (field, _createdBy.timestamp) composites in firestore.indexes.json.
 *
 * @param {object} params
 * @param {string} [params.projectDocId]
 * @param {string} [params.kind]
 * @param {boolean} [params.includeEmpty]
 * @returns {Promise<Array<object>>} Lot models, newest first.
 */
export default async function fetchLotsForOverview({
  projectDocId = null,
  kind = 'all',
  includeEmpty = false,
}) {
  const queryParts = [
    collection(getFirestore(), 'lots'),
    where('status', 'in', includeEmpty
      ? [...ACTIVE_STATUSES, LotStatus.EMPTY]
      : ACTIVE_STATUSES),
  ];
  if (projectDocId) {
    queryParts.push(where('projectDocId', '==', projectDocId));
  }
  if (kind !== 'all') {
    queryParts.push(where('kind', '==', kind));
  }
  queryParts.push(orderBy('_createdBy.timestamp', 'desc'));
  const snapshot = await getDocs(query(...queryParts));
  return snapshot.docs.map((doc) => lotFromFirestore(doc));
}
